import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import _ from 'lodash';
import { DatatableUtil } from '@xengage/gw-portals-util-js';
import { useTranslator } from '@jutro/locale';
import { UWIssuesUtil } from "premier-policycommon-util-js";
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import PrintDocumentsComponent from "premier-capability-policyjob-react/components/PrintDocumentsComponent/PrintDocumentsComponent";
import FormsComponent from "premier-capability-policyjob-react/components/FormComponent/FormComponent";
import QuoteValidityComponent from "premier-capability-policyjob-react/components/QuoteValidityComponent/QuoteValidityComponent";
import QuoteDetailsComponent from 'premier-capability-policyjob-react/components/QuoteDetailsComponent/QuoteDetailsComponent';
import presentationMetadata from './QuotePage.metadata.json5';
import styles from './QuotePage.module.scss';
import messages from './QuotePage.messages';
import CoveragesPageComponent from "../Coverages/CoveragesPage";
import { TextInput } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';


function QuotePage(props) {
    const {
        wizardData: submissionVM,
        steps,
        jumpTo,
        currentStep,
        wizardData, updateWizardData,
        underwritingIssues
    } = props;

    const breakpoint = useContext(BreakpointTrackerContext);
    const { pageKey } = currentStep;
    const isQuoteBlocked = UWIssuesUtil.isQuoteBlocked(underwritingIssues);
    const findBlockingPoint = UWIssuesUtil.findBlockingPoint(underwritingIssues);
    const underWritingIssuesNonBlocking = UWIssuesUtil.UWIssuesNonBlocking(underwritingIssues);
    const translator = useTranslator();
    const selectOffering = submissionVM.value.lobData.consTraAndServLine.selectedOffering_Ext
    const { authHeader } = useAuthentication();

    const periodStartDate = _.get(submissionVM.value, 'baseData.periodStartDate');

    // eslint-disable-next-line security/detect-object-injection
    const metadata = presentationMetadata[pageKey];

    const handleNext = useCallback(() => {
        const offeredQuotes = _.get(submissionVM.value, 'quoteData.offeredQuotes[0].publicID');

        _.set(submissionVM, 'bindData.chosenQuote', offeredQuotes);

        return submissionVM;
    }, [submissionVM]);

    const navigateToCoverages = useCallback(() => {
        let redirectPath = ''

        switch (selectOffering) {
            case 'EquBre':
                redirectPath = '/EquipmentBreakdown';
                break;
            case 'BuiRisAllTyp':
                redirectPath = '/BuildersRisk'
                break;
            default:
                break;
        }

        const indexOfGeneralCoverages = _.findIndex(
            steps,
            ({ path }) => path === redirectPath
        );

        jumpTo(indexOfGeneralCoverages);
    }, [jumpTo, selectOffering, steps]);

    // eslint-disable-next-line arrow-body-style
    const getCell = (item, _index, property) => {
        return item[property.id];
    };


    const getFormTableData = useMemo(() => {
        const forms = submissionVM?.value?.lobData?.consTraAndServLine?.lineCoverages?.forms_Ext;

        return forms?.map(({ formDes, formCategory }) => ({
            formDes,
            formCategory,
        })) || [];
    }, [submissionVM]);



    const getInternalUseOnlyConditionsOfQuoteTableData = useCallback(() => {
        const coverables = submissionVM?.value?.lobData?.consTraAndServLine?.coverables;

        if (selectOffering === 'EquBre') {
            const conditions = coverables?.equipmentBreakdown?.internalUseOnly?.pctsubofQuoExps || [];

            return conditions.map((item) => ({
                conFieConditionsOfQuote: item,
            }));
        }

        if (selectOffering === 'BuiRisAllTyp') {
            const conditions = coverables?.buildersRisk?.internalUseOnly?.pctsubofQuoExps || [];

            return conditions.map((item) => ({
                conFieConditionsOfQuote: item,
            }));
        }

        return [];
    }, [submissionVM, selectOffering]);

    const getInternalUseOnlyAdditionalConditionsTableData = useCallback(() => {
        const coverables = submissionVM?.value?.lobData?.consTraAndServLine?.coverables;

        if (selectOffering === 'EquBre') {
            const additionalConditions = coverables?.equipmentBreakdown?.internalUseOnly?.pctaddConofPolExps || [];

            return additionalConditions.map((item) => ({
                conFieAdditionalConditions: item,
            }));
        }

        if (selectOffering === 'BuiRisAllTyp') {
            const additionalConditions = coverables?.buildersRisk?.internalUseOnly?.pctaddConofPolExps || [];

            return additionalConditions.map((item) => ({
                conFieAdditionalConditions: item,
            }));
        }

        return [];
    }, [submissionVM, selectOffering]);


    const hasInternalUseOnlyData = (internalUseOnly) => (
        internalUseOnly?.pctsubofQuoExps?.length > 0 ||
        internalUseOnly?.pctaddConofPolExps?.length > 0
    );

    const getInternalUseOnlyAccordion = () => {
        const coverables = submissionVM?.value?.lobData?.consTraAndServLine?.coverables;

        if (selectOffering === 'EquBre') {
            return hasInternalUseOnlyData(coverables?.equipmentBreakdown?.internalUseOnly);
        }

        if (selectOffering === 'BuiRisAllTyp') {
            return hasInternalUseOnlyData(coverables?.buildersRisk?.internalUseOnly);
        }

        return false;
    };

    const getInternalUseOnlyConditionsOfQuoteContainer = useCallback(() => {
        const { coverables } = submissionVM.value.lobData.consTraAndServLine

        const equipmentBreakdownVisible = coverables?.equipmentBreakdown?.internalUseOnly?.pctsubofQuoExps?.length > 0;
        const buildersRiskVisible = coverables?.buildersRisk?.internalUseOnly?.pctsubofQuoExps?.length > 0;

        if (selectOffering === 'EquBre') {
            return equipmentBreakdownVisible
        }

        return buildersRiskVisible
    }, [selectOffering, submissionVM.value.lobData.consTraAndServLine])

    const getInternalUseOnlyAdditionalConditionsContainer = useCallback(() => {
        const { coverables } = submissionVM.value.lobData.consTraAndServLine

        const equipmentBreakdownVisible = coverables?.equipmentBreakdown?.internalUseOnly?.pctaddConofPolExps?.length > 0
        const buildersRiskVisible = coverables?.buildersRisk?.internalUseOnly?.pctsubofQuoExps?.length > 0;

        if (selectOffering === 'EquBre') {
            return equipmentBreakdownVisible
        }

        return buildersRiskVisible
    }, [selectOffering, submissionVM.value.lobData.consTraAndServLine]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            navigateToCoverages,
            getCell,
            sortString: DatatableUtil.sortString
        },
        resolveComponentMap: {
            coveragespagecomponent: CoveragesPageComponent,
            quoteDetailsComponent: QuoteDetailsComponent,
            quoteValidityComponent: QuoteValidityComponent,
            PrintDocumentsComponent,
            FormsComponent,
            TextInput
        }
    };

    const generatelocRiskseOverrides = () => {
        const overrides = submissionVM.value.lobData.consTraAndServLine.coverables.locRisks.map((locRisk, index) => ({
            [`locRiskSection${index}`]: {
                title: locRisk.accountLocFie.displayName
            },
            [`locRiskCoverageInfoComponent${index}`]: {
                wizardData,
                updateWizardData,
                clausesBasePath: `lobData.consTraAndServLine.lineCoverages.locRiskCoverages.children[${index}].coverages`,
                title: "Coverages",
                clasueType: 'coverages',
                isEditable: false
            }
        }));

        return Object.assign({}, ...overrides);
    };


    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            readOnly: true
        },
        underWritingIssues: {
            underwritingIssues: underWritingIssuesNonBlocking,
            blockQuote: findBlockingPoint,
            filterUWIssuesInCustomOffering: false,
            quoteID: _.get(submissionVM.value, 'quoteID'),
            accountNumber: _.get(submissionVM.value, 'baseData.accountNumber')
        },
        liabilityCoverageAccordion: {
            isEditable: false,
            readOnly: true
        },
        liabilityCoverageInfoComponent: {
            wizardData,
            updateWizardData,
            clausesBasePath: `lobData.consTraAndServLine.lineCoverages.coverages`,
            title: "Coverages",
            clasueType: 'coverages',
            isEditable: false,
            pathToGet: 'consTraAndServLine.coverages'
        },
        quoteDetailsComponent: {
            wizardData
        },
        quoteValidityComponent: {
            wizardData
        },
        policyQuoteValidityDays: {
            content: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
        },
        policyQuoteValDaysHead: {
            content: translator(messages.ctQuoteValidityDays, {
                quoteValidityDays: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
            })
        },
        tableFormSummary: {
            data: getFormTableData
        },
        tableInternalUseOnlyConditionsOfQuote: {
            data: getInternalUseOnlyConditionsOfQuoteTableData()
        },
        tableInternalUseOnlyAdditionalConditions: {
            data: getInternalUseOnlyAdditionalConditionsTableData()
        },
        internalUseOnlyAccordion: {
            visible: getInternalUseOnlyAccordion()
        },
        internalUseOnlyConditionsOfQuoteContainer: {
            visible: getInternalUseOnlyConditionsOfQuoteContainer()
        },
        internalUseOnlyAdditionalConditionsContainer: {
            visible: getInternalUseOnlyAdditionalConditionsContainer()
        },
        taxes: {
            visible: _.get(submissionVM.value, 'baseData.baseState_Ext') === 'QC',
        },
        fees: {
            visible: _.get(submissionVM.value, 'quoteData.offeredQuotes') &&
                _.get(submissionVM.value, 'quoteData.offeredQuotes[0].premium.fees.amount') !== 0
        },
        policyfees: {
            visible: _.get(submissionVM.value, 'baseData.policyFee_Ext.amount') !== 0
        },
        inspectionFee: {
            visible: _.get(submissionVM.value, 'baseData.inspectionFee_Ext.amount') !== 0
        },
        extensionFee: {
            visible: _.get(submissionVM.value, 'baseData.extensionFee_Ext.amount') !== 0
        },
        startDate: {
            value: new Date(periodStartDate.year, periodStartDate.month, periodStartDate.day).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: '2-digit' })
        },
        PrintDocumentsComponent: {
            visible: submissionVM.value.baseData.periodStatus === 'Quoted',
            jobNumber: submissionVM.value.quoteID,
            authHeader
        },
        FormsComponent: {
            forms: submissionVM.value.lobData.consTraAndServLine.lineCoverages.forms_Ext
        },
        ...generatelocRiskseOverrides()
    };

    useEffect(() => { }, []);

    return (
        <WizardPage
            disableNext={underWritingIssuesNonBlocking?.length > 0}
            onNext={handleNext}>
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
